import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Cookie, X } from 'lucide-react'

export function CookieBanner() {
	const [isVisible, setIsVisible] = useState(false)

	useEffect(() => {
		const consent = localStorage.getItem('cookie-consent')
		if (!consent) {
			// Small delay before showing to notice the content first
			const timer = setTimeout(() => setIsVisible(true), 2000)
			return () => clearTimeout(timer)
		}
	}, [])

	const handleConsent = (accepted: boolean) => {
		localStorage.setItem('cookie-consent', accepted ? 'accepted' : 'declined')
		setIsVisible(false)
	}

	return (
		<AnimatePresence>
			{isVisible && (
				<motion.div
					initial={{ y: 100, opacity: 0 }}
					animate={{ y: 0, opacity: 1 }}
					exit={{ y: 100, opacity: 0 }}
					transition={{ duration: 0.5, ease: 'easeOut' }}
					className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6 pointer-events-none"
				>
					<div className="max-w-4xl mx-auto pointer-events-auto">
						<div className="relative overflow-hidden bg-surface-primary/80 dark:bg-surface-dark-primary/90 backdrop-blur-xl border border-white/20 dark:border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.12)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.4)] rounded-2xl md:rounded-[2rem]">
							{/* Subtle gradient accent */}
							<div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-secondary via-primary to-secondary opacity-50" />

							<div className="flex flex-col md:flex-row items-center gap-4 md:gap-8 p-5 md:p-6">
								{/* Icon & Text Section */}
								<div className="flex items-start md:items-center gap-4 flex-grow">
									<div className="hidden sm:flex flex-shrink-0 w-12 h-12 items-center justify-center rounded-2xl bg-secondary/10 dark:bg-secondary/20 text-secondary">
										<Cookie size={28} />
									</div>
									<div className="flex flex-col gap-1">
										<h4 className="text-on-surface dark:text-on-surface-dark font-bold text-sm md:text-base leading-tight">
											Nós valorizamos sua privacidade
										</h4>
										<p className="text-on-surface-subtle dark:text-on-surface-dark-muted text-xs md:text-sm leading-relaxed max-w-2xl">
											Utilizamos cookies para oferecer uma experiência personalizada e analisar nosso tráfego conforme a {' '}
											<strong className="text-primary-dark dark:text-secondary whitespace-nowrap">LGPD (Lei Geral de Proteção de Dados)</strong>.
										</p>
									</div>
								</div>

								{/* Actions Section */}
								<div className="flex items-center gap-3 w-full md:w-auto">
									<button
										onClick={() => handleConsent(false)}
										className="flex-1 md:flex-none px-5 py-2.5 rounded-xl border border-border-default dark:border-white/10 text-on-surface-subtle dark:text-on-surface-dark-muted text-xs md:text-sm font-bold hover:bg-surface-alt dark:hover:bg-white/5 transition-colors"
									>
										Recusar
									</button>
									<button
										onClick={() => handleConsent(true)}
										className="flex-1 md:flex-none px-6 py-2.5 bg-secondary hover:bg-secondary/90 text-primary font-bold text-xs md:text-sm rounded-xl shadow-lg shadow-secondary/20 transition-all transform active:scale-95"
									>
										Aceitar todos
									</button>

									<button
										onClick={() => setIsVisible(false)}
										className="hidden md:flex flex-shrink-0 p-2 text-on-surface-subtle dark:text-on-surface-dark-muted hover:text-on-surface dark:hover:text-on-surface-dark transition-colors"
										aria-label="Fechar"
									>
										<X size={18} />
									</button>
								</div>
							</div>
						</div>
					</div>
				</motion.div>
			)}
		</AnimatePresence>
	)
}
